// ═══════════════════════════════════════════════════════════════════════════════
//  src/components/onboarding/onboardingSpeech.js
//  MICROSERVICIO ONBOARDING v3 — Audio del banco de calibración
//
//  PUNTO DE DECISIÓN B (docs/onboarding-master-v2.md, Parte IV §2) — RESUELTO:
//  ────────────────────────────────────────────────────────────────────────────
//  Se usa la Web Speech API nativa del navegador, NO el pipeline de TTS del
//  tutor. Razones, contra las preguntas que el documento deja abiertas:
//
//   1. El requisito no negociable es "sonar en menos de 1-2 segundos desde que
//      el alumno toca play". Web Speech arranca en milisegundos, en el propio
//      dispositivo. El pipeline REST Híbrido del tutor demora ~15s: rompería
//      la promesa de "calibración de 60 segundos" de Parte I §5.1.
//   2. `tts.py` es ZONA ROJA del Protocolo Central NSL §2. Esta decisión no lo
//      toca, no lo importa y no lo reutiliza — ni siquiera indirectamente.
//   3. El repo YA tiene este patrón funcionando en producción
//      (src/exercises/webSpeechUtils.js), así que no se introduce tecnología
//      nueva ni una dependencia más: se sigue una convención existente.
//   4. Cero archivos de audio en el repo → sin impacto en el tamaño del build
//      de Vercel, y sin un paso de generación previa que mantener.
//
//  ⚠️  Este archivo es una implementación INDEPENDIENTE, no un import de
//      webSpeechUtils.js. A propósito: ese archivo sirve a los ejercicios del
//      core y ajustarlo para las necesidades de la calibración (tono variable
//      por slide) sería tocar código del que dependen ejercicios en producción.
//      Aislar cuesta ~60 líneas y elimina ese riesgo por completo.
// ═══════════════════════════════════════════════════════════════════════════════

let cachedVoice = null;

/** Voz en inglés preferida, con degradación ordenada. */
function getEnglishVoice() {
  if (cachedVoice) return cachedVoice;
  const voices = window.speechSynthesis?.getVoices() ?? [];
  cachedVoice =
    voices.find((v) => v.name === 'Google UK English Female') ||
    voices.find((v) => v.lang === 'en-GB' && v.name.toLowerCase().includes('female')) ||
    voices.find((v) => v.lang === 'en-GB') ||
    voices.find((v) => v.lang?.startsWith('en-')) ||
    null;
  return cachedVoice;
}

// Chrome carga las voces de forma asíncrona: invalidamos el caché al cambiar.
if (typeof window !== 'undefined' && window.speechSynthesis) {
  window.speechSynthesis.onvoiceschanged = () => {
    cachedVoice = null;
  };
  window.speechSynthesis.getVoices();
}

/** ¿El navegador puede reproducir estos audios? */
export function isSpeechAvailable() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Reproduce el texto de un slide de audio.
 *
 * @param {string} text            texto en inglés del banco (calibrationBank.js)
 * @param {Object} voice           { rate, pitch } — transmite el tono que pide
 *                                 el documento (sarcasmo, calidez, indiferencia)
 * @param {Object} cb              { onStart, onEnd, onError }
 */
export function speakSlide(text, voice = {}, { onStart, onEnd, onError } = {}) {
  if (!isSpeechAvailable() || !text) {
    onError?.('speech-unavailable');
    return;
  }

  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'en-GB';
  utterance.rate = voice.rate ?? 0.9;
  utterance.pitch = voice.pitch ?? 1.0;
  utterance.volume = 1.0;

  const v = getEnglishVoice();
  if (v) utterance.voice = v;

  utterance.onstart = () => onStart?.();
  utterance.onend = () => onEnd?.();
  utterance.onerror = (e) => onError?.(e?.error ?? 'speech-error');

  // Chrome recolecta la utterance si nadie la referencia: la anclamos.
  window._nslOnboardingUtterance = utterance;
  window.speechSynthesis.speak(utterance);
}

/** Corta cualquier reproducción en curso (al desmontar o cambiar de slide). */
export function cancelSpeech() {
  if (isSpeechAvailable()) window.speechSynthesis.cancel();
}
